"use strict";

/**
 * <strong>Purpose:</strong> <br>
 * Provides utilities for working with standard MathWorks widgets including Form, Menu, Toolstrip, &
 * Gallery widgets.
 * <br><br>
 *
 * @module mw-utils/widgetUtils
 *
 * @copyright Copyright 2015 The MathWorks, Inc.
 */

define(["dijit/registry", "mw-utils/domUtils"], function (registry, domUtils) {

    var widgetUtils = {};

    /**
     * Returns the ancestor widget which matches the selector, or null if none is found.
     * @param widget
     * @param selector
     * @returns {*}
     */
    widgetUtils.findParentWidget = function (widget, /*String*/selector) {
        var parent = null,
            parentPopupId;

        if (widget instanceof HTMLElement) {
            widget = registry.getEnclosingWidget(widget);
        }

        while (widget) {
            if (widget.get("type") === selector) {
                parent = widget;
                break;
            }

            if (domUtils.containsClass(widget.domNode, selector)) {
                parent = widget;
                break;
            }

            parentPopupId = domUtils.getAttr(widget.domNode.parentNode, "dijitpopupparent");

            // If the widget is in one of the popups, then go up the popup parent chain
            if (parentPopupId) {
                widget = registry.byId(parentPopupId);
                // If its not one of the popups, then go to parent
            } else {
                widget = widget.getParent();
            }
        }

        return parent;
    };

    /**
     * Indicates if the input widget is a menu or a menu child
     * @param widget
     * @returns {boolean}
     */
    widgetUtils.isMenuChild = function (widget) {
        // Since all menus/popups/dropdowns/popuplists etc... currently use popup.js, this logic works
        return domUtils.findParentNode(widget, "dijitPopup") != null;
    };

    /**
     * Indicates if the input widget is a menu or a menu child
     * @param widget
     * @returns {widget}
     */
    widgetUtils.getParentMenu = function (widget) {
        // Since all menus/popups/dropdowns/popuplists etc... currently use popup.js, this logic works
        var dijitPopup = domUtils.findParentNode(widget, "dijitPopup");

        if (!dijitPopup) {
            return null;
        }

        return registry.getEnclosingWidget(dijitPopup.children[0]);
    };

    /**
     * Indicates whether a widget is an invoking widget for any menu if only one argument is passed,
     * or if it is the invoking widget for the specific menu if two arguments are passed.
     * @param widget
     * @param menu (optional)
     * @returns {boolean}
     */
    widgetUtils.isInvokingWidget = function (widget, menu) {
        if (menu) {
            return widgetUtils.getMenuWidget(widget) === menu;
        } else {
            return widgetUtils.getMenuWidget(widget) != null;
        }
    };

    /**
     * Returns the invoking widget for a popup menu
     * @param widget
     * @returns {widget}
     */
    widgetUtils.getInvokingWidget = function (widget) {
        if (widget._invokingWidget) {
            return widget._invokingWidget;
        }

        var dijitPopup = domUtils.findParentNode(widget, "dijitPopup");

        if (!dijitPopup) {
            return null;
        }

        return registry.getEnclosingWidget(document.getElementById(dijitPopup.getAttribute("dijitpopupparent")));
    };

    widgetUtils.getMenuWidget = function (widget) {
        return widget.menu || widget.dropDown;
    };

    /**
     * Indicates whether the menu associated with the input widget is open.
     * @param widget - a menu, an invoking widget of a menu, or a child of a menu.
     * @returns {boolean}
     */
    widgetUtils.isMenuOpen = function (widget) {
        if (widget == null) {
            return false;
        }
        var primaryNode,
            primaryWidget = widget;
        // TODO: Update logic when CompositionMixin is added
        // If the input is a child in a composite widget, then get the primary node from the value of the "data-composite-child" attribute
        if (widget.domNode && widget.domNode.hasAttribute("data-composite-child")) {
            primaryNode = document.getElementById(widget.domNode.getAttribute("data-composite-primary-id"));
            primaryWidget = registry.getEnclosingWidget(primaryNode);
        }
        return primaryWidget && primaryWidget.dropDown && ( // HasDropDown & open
        domUtils.containsClass(primaryWidget.domNode, "dijitOpened") || domUtils.containsClass(primaryWidget.domNode, "dijitHasDropDownOpen")) || primaryWidget && primaryWidget.isMenuOpen && primaryWidget.isMenuOpen() || // (MenuMixin || MW/menu/Menu) & open
        primaryWidget && primaryWidget.domNode && domUtils.findParentNode(primaryWidget, "dijitPopup") && (primaryWidget.getParent && primaryWidget.getParent() && // (child of MW/menu/Menu) & open
        primaryWidget.getParent().isMenuOpen && primaryWidget.getParent().isMenuOpen() || domUtils.getStyle(domUtils.findParentNode(primaryWidget, "dijitPopup"), "display") !== "none"); // (child of popup.js) & open
    };

    /**
     * Indicates if the child is a descendant or in-law of the ancestor
     * @param child
     * @param ancestor
     * @returns {*}
     */
    widgetUtils.isDescendant = function (child, ancestor) {
        if (child == null) {
            return false;
        } else if (child.domNode) {
            child = child.domNode;
        }

        if (ancestor == null) {
            return false;
        } else if (ancestor.domNode) {
            ancestor = ancestor.domNode;
        }

        var getSpouseNode = function getSpouseNode(childNode) {
            var spouse,
                childWidget = registry.getEnclosingWidget(childNode);
            if (childWidget && widgetUtils.isMenuChild(childWidget) && widgetUtils.isMenuOpen(childWidget)) {
                spouse = widgetUtils.getInvokingWidget(childWidget);
                if (spouse && spouse.domNode) {
                    return spouse.domNode;
                }
            }
            // return null by default
            return null;
        };

        // If node is a direct descendant or the child of a sub-menu then return true
        return domUtils.isDescendantOrInLaw(child, ancestor, getSpouseNode, true);
    };

    widgetUtils.getPrimaryWidget = function (widget) {
        var node;

        if (widget.domNode) {
            node = widget.domNode;
        } else if (widget instanceof HTMLElement) {
            node = widget;
        } else {
            throw new Error('The input value must be a widget or HTML Element.');
        }

        // TODO: Update logic when CompositionMixin is added
        // If the input is a child in a composite widget, then get the primary node from the value of the "data-composite-child" attribute
        if (node && node.hasAttribute("data-composite-child")) {
            return registry.getEnclosingWidget(domUtils.getById(node.getAttribute("data-composite-primary-id")));
        }

        return widget;
    };

    return widgetUtils;
});

